import { memo, useEffect, useState, type PropsWithChildren } from 'react';
import { Helmet } from 'react-helmet';
import { GlobalFooter } from '@fff-web/fff-global-footer';
import { GlobalNav } from '@fff-web/fff-global-nav';
import { LoadingSpinner } from '@fff-web/fff-ui-shared';
import { isLoggedIn } from '../../services/auth';

type LayoutProps = {
  pageTitle: string;
  meta?: {
    links?: { href: string; [key: string]: unknown }[];
    scripts?: { src: string; [key: string]: unknown }[];
  };
  isLoading?: boolean;
  redirectIfNoAuth?: boolean;
};

const Layout = ({
  children,
  pageTitle = 'FabFitFun',
  meta = {},
  isLoading = false,
  redirectIfNoAuth = false,
}: PropsWithChildren<LayoutProps>) => {
  const [hasPermissions, setHasPermissions] = useState(!redirectIfNoAuth);

  useEffect(() => {
    if (redirectIfNoAuth) {
      isLoggedIn().then(({ ok }) => {
        if (!ok) {
          const url = `${process.env.REACT_APP_LOGIN_URL}${window.location.href}`;

          window.location.replace(url);
        } else {
          setHasPermissions(true);
        }
      });
    }
  }, [redirectIfNoAuth]);

  return (
    hasPermissions && (
      <>
        <Helmet>
          <title key="page-title">{pageTitle}</title>
          {meta?.links?.length &&
            meta.links.map(({ href, ...link }, index) => (
              <link
                key={href ? href + index : index}
                {...link}
                href={href}
                rel="stylesheet"
                type="text/css"
              />
            ))}
          {meta?.scripts?.length &&
            meta.scripts.map(({ src, ...script }, index) => (
              <script key={src ? src + index : index} {...script} src={src} />
            ))}
        </Helmet>
        <MemoizedGlobalNav />
        {isLoading ? <LoadingSpinner /> : children}
        <GlobalFooter chatbotConfig={{ agentId: process.env.REACT_APP_APPLIED_LABS_AGENT_ID }} />
      </>
    )
  );
};

export default Layout;

const MemoizedGlobalNav = memo(function MemoizedGlobalNav() {
  const [reRender, setReRender] = useState(false);

  useEffect(() => {
    const handleReRender = () => setReRender(!reRender);
    window.addEventListener('re-render GlobalNav', handleReRender);

    return () => {
      window.removeEventListener('re-render GlobalNav', handleReRender);
    };
  }, [reRender]);

  return <GlobalNav env={process.env.REACT_APP_ENVIRONMENT} />;
});
